/**
 * What somebody is playing, right now.
 *
 * Reported by the desktop app, which compares running processes against the
 * games in the catalogue and sends nothing else - see electron/games.js. The
 * web build has nothing to watch, so people on it simply never light up.
 *
 * Absent rather than empty when nobody is playing: a permanent "not playing
 * anything" card is a row of furniture that is wrong most of the time.
 */

import { useEffect, useState } from 'react';
import type { PublicUser } from '@/lib/types';
import { useSession } from '@/stores/session';
import { gameIconArt } from '@/lib/gameArt';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';

/** How long they have been in it, counted up while you watch. */
function elapsed(since: number | null | undefined): string | null {
  if (!since) return null;
  const mins = Math.floor((Date.now() - since) / 60_000);
  if (mins < 1) return 'just started';
  if (mins < 60) return mins + ' min';
  const hours = Math.floor(mins / 60);
  return hours + 'h ' + (mins % 60) + 'm';
}

export function NowPlaying({ user, compact }: { user: PublicUser; compact?: boolean }) {
  const catalog = useSession((s) => s.catalog);
  const [, tick] = useState(0);

  // Re-renders once a minute so the timer moves without a socket message.
  useEffect(() => {
    if (!user.playing) return;
    const t = setInterval(() => tick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, [user.playing]);
  const catalogGames = catalog?.games;

  /*
   * Everything they have open.
   *
   * People run two at once - a game and the thing they are half-watching on
   * the second monitor - and showing whichever the process table happened to
   * list first made the card say something arbitrary. The first is drawn
   * large, the rest as marks beside it.
   */
  const open = (user.playingAll?.length ? user.playingAll : user.playing ? [user.playing] : [])
    .filter((id) => !!catalog?.games.some((g) => g.id === id));
  if (!open.length) return null;

  const first = open[0]!;
  const game = catalog?.games.find((g) => g.id === first);
  const art = gameIconArt(first);
  const colour = game?.color ?? 'var(--accent)';
  const since = elapsed(user.playingSince);
  const others = open.slice(1);

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-[var(--radius)] border',
        compact ? 'px-2.5 py-2' : 'p-3.5'
      )}
      style={{
        borderColor: 'color-mix(in srgb, ' + colour + ' 34%, transparent)',
        background: 'color-mix(in srgb, ' + colour + ' 9%, transparent)'
      }}
    >
      <span
        className={cn(
          'grid flex-none place-items-center overflow-hidden rounded-[calc(10px*var(--radius-mul,1))] border',
          compact ? 'h-8 w-8' : 'h-11 w-11'
        )}
        style={{
          borderColor: 'color-mix(in srgb, ' + colour + ' 40%, transparent)',
          background: 'color-mix(in srgb, var(--bg-0) 55%, transparent)',
          color: colour
        }}
      >
        {art
          ? <img src={art} alt="" className="h-full w-full object-cover" />
          : <Icon name={game?.icon ?? 'dice'} size={compact ? 16 : 20} />}
      </span>

      <span className="min-w-0 flex-1">
        <span
          className="block text-[10px] font-extrabold tracking-[.1em] uppercase"
          style={{ color: colour }}
        >
          Playing
        </span>
        <span className={cn('block truncate font-bold', compact ? 'text-[12.5px]' : 'text-[14px]')}>
          {game?.name ?? first}
        </span>
        {since && !compact ? (
          <span className="block text-[11.5px] text-mute">for {since}</span>
        ) : null}
      </span>

      {/* The others, as their own marks. Named in the title rather than
          written out: the card is one line tall and a list of three game
          names does not fit beside a timer. */}
      {others.length ? (
        <span
          className="flex flex-none items-center"
          title={'Also open: ' + others.map((id) => catalogGames?.find((g) => g.id === id)?.name ?? id).join(', ')}
        >
          {others.slice(0, 3).map((id, i) => {
            const meta = catalogGames?.find((g) => g.id === id);
            const icon = gameIconArt(id);
            return (
              <span
                key={id}
                className={cn(
                  'grid flex-none place-items-center overflow-hidden rounded-[calc(8px*var(--radius-mul,1))] ring-2 ring-[var(--bg-1)]',
                  compact ? 'h-5 w-5' : 'h-7 w-7'
                )}
                style={{
                  marginLeft: i ? -6 : 0,
                  background: 'color-mix(in srgb, var(--bg-0) 55%, transparent)',
                  color: meta?.color ?? 'var(--accent)'
                }}
              >
                {icon
                  ? <img src={icon} alt="" className="h-full w-full object-cover" />
                  : <Icon name={meta?.icon ?? 'dice'} size={compact ? 11 : 14} />}
              </span>
            );
          })}
        </span>
      ) : null}

      {/* The one moving thing on the card, because "right now" is the point. */}
      <span className="relative flex h-2 w-2 flex-none">
        <span
          className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-70"
          style={{ background: colour }}
        />
        <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: colour }} />
      </span>
    </div>
  );
}
